import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, brl } from "@/lib/api";
import { useHideValues } from "@/components/HideValues";
import { UserCheck, UserX } from "lucide-react";
import { SOURCE_LABELS } from "@/lib/sources";

function MiniKpi({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl lg:rounded-xl border border-card2-border bg-card2 p-5 lg:p-3 flex items-center gap-4 lg:gap-3">
      <div className="h-12 w-12 lg:h-8 lg:w-8 shrink-0 rounded-xl lg:rounded-lg flex items-center justify-center" style={{ background: "rgb(var(--c-brand-rgb)/0.15)", color: "var(--c-brand-neon)" }}><Icon className="h-6 w-6 lg:h-4 lg:w-4" /></div>
      <div className="min-w-0"><div className="text-xs lg:text-[11px] font-mono-caps text-zinc-500 truncate">{label}</div><div className="font-display text-2xl lg:text-lg font-black text-zinc-100">{value}</div></div>
    </div>
  );
}

const goalColor = (p) => (p >= 100 ? "#4ade80" : p >= 60 ? "#facc15" : "#f87171");

function ShopGoalGauge({ g }) {
  const { hide } = useHideValues();
  const pct = g?.goal ? Math.round((g.realized / g.goal) * 100) : 0;
  const R = 52, C = 2 * Math.PI * R, shown = Math.min(pct, 100);
  const col = goalColor(pct);
  return (
    <section data-testid="goals-shop" className="h-full rounded-2xl border border-card2-border bg-card2 p-5">
      <h3 className="font-display text-base font-bold uppercase text-zinc-100 mb-4">Meta da oficina</h3>
      {!g?.goal ? <p className="text-sm text-zinc-500">Defina a meta de faturamento do mês logo acima.</p> : (
        <div className="flex items-center gap-5 flex-wrap">
          <svg width="132" height="132" viewBox="0 0 132 132" className="shrink-0">
            <circle cx="66" cy="66" r={R} fill="none" stroke="rgb(39 39 42)" strokeWidth="14" />
            <circle cx="66" cy="66" r={R} fill="none" stroke={col} strokeWidth="14" strokeLinecap="round" strokeDasharray={`${(shown / 100) * C} ${C}`} transform="rotate(-90 66 66)" style={{ transition: "stroke-dasharray .6s", filter: `drop-shadow(0 0 6px ${col}88)` }} />
            <text x="66" y="72" textAnchor="middle" fill="#f4f4f5" fontWeight="900" fontSize="26">{pct}%</text>
          </svg>
          <div className="space-y-1 text-sm">
            <div><span className="text-zinc-500">Realizado </span><b data-testid="goals-shop-realized" className="text-zinc-100">{hide ? "•••••" : brl(g.realized)}</b></div>
            <div><span className="text-zinc-500">Meta </span><b className="text-zinc-100">{hide ? "•••••" : brl(g.goal)}</b></div>
            <div><span className="text-zinc-500">Falta </span><b className="text-zinc-100">{hide ? "•••••" : brl(Math.max(g.goal - g.realized, 0))}</b></div>
          </div>
        </div>
      )}
    </section>
  );
}

function PeopleGoalBars({ rows }) {
  const { hide } = useHideValues();
  return (
    <section data-testid="goals-people" className="h-full rounded-2xl border border-card2-border bg-card2 p-5">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <h3 className="font-display text-base font-bold uppercase text-zinc-100">Equipe x meta</h3>
        <div className="flex gap-3 text-[11px] text-zinc-400"><span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm border-2 border-sky-400" />Mecânico</span><span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm border-2 border-fuchsia-400" />Consultor</span></div>
      </div>
      <div className="flex items-end gap-3 overflow-x-auto pb-1">
        {rows.map((m) => {
          const pct = Math.round(m.pct || 0);
          const col = goalColor(pct);
          const ring = m.role === "consultor" ? "#e879f9" : "#38bdf8";
          return (
            <div key={`${m.role}-${m.name}`} data-testid={`goal-bar-${m.role}-${m.name}`} className="flex-1 min-w-[4.5rem] flex flex-col items-center gap-1.5">
              <div className="font-display text-lg font-black" style={{ color: col }}>{pct}%</div>
              <div className="relative w-full h-32 rounded-md bg-zinc-900 overflow-hidden" title={hide ? `${pct}%` : `${brl(m.produced)} de ${brl(m.goal)}`}>
                <div className="absolute inset-x-0 top-0 border-t-2 border-dashed border-zinc-600" />
                <div className="absolute inset-x-0 bottom-0 rounded-t-md transition-[height] duration-700" style={{ height: `${Math.min(pct, 100)}%`, minHeight: 3, background: col, boxShadow: `0 -8px 18px -8px ${col}` }} />
              </div>
              <div className="w-full text-center text-[11px] font-bold text-zinc-200 truncate border-b-2 pb-0.5" style={{ borderColor: ring }}>{m.name}</div>
              <div className="text-[10px] text-zinc-500 tabular-nums">{hide ? "•••••" : brl(m.produced)}</div>
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-[10px] text-zinc-500">Linha tracejada = 100% da meta.</p>
    </section>
  );
}

export function GoalsSection({ shopGoal, scopeKey }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/team/goals").then(({ data }) => setD(data)).catch(() => setD({ mechanics: [], consultants: [] })); }, [scopeKey]);
  const rows = [...(d?.mechanics || []), ...(d?.consultants || [])].filter((m) => m.goal > 0);
  return (
    <Section title="Metas do mês" testid="hub-section-goals">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <ShopGoalGauge g={shopGoal} />
        <div className="lg:col-span-2">
          {d && !rows.length ? (
            <section data-testid="goals-empty" className="h-full rounded-2xl border border-card2-border bg-card2 p-5 text-sm text-zinc-400">
              Nenhum mecânico ou consultor tem meta mensal. <Link to="/owner/funcionarios" data-testid="goals-empty-link" className="text-green-300 hover:underline">Cadastrar metas em Funcionários →</Link>
            </section>
          ) : <PeopleGoalBars rows={rows} />}
        </div>
      </div>
    </Section>
  );
}

export function ClientSourcesSection({ scopeKey }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    const to = new Date().toLocaleDateString("sv-SE");
    api.get("/dashboard", { params: { from: to.slice(0, 8) + "01", to } }).then(({ data }) => setD(data)).catch(() => setD({}));
  }, [scopeKey]);
  const v = (x) => (d ? (x ?? 0) : "…");
  return (
    <Section title="Origem dos clientes · este mês" testid="hub-section-sources">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <CustomerMixChart mix={d?.customer_mix} />
        <SourceDonutChart bySource={d?.by_source} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <MiniKpi testid="hub-kpi-active" icon={UserCheck} label="Clientes ativos" value={v(d?.active_customers)} />
        <MiniKpi testid="hub-kpi-lost" icon={UserX} label="Clientes sumidos" value={v(d?.lost_customers)} />
      </div>
    </Section>
  );
}

const MIX_COLORS = { novo: "#4ade80", antigo: "#60a5fa", retorno: "#facc15", retrabalho: "#f87171" };
const MIX_LABELS = { novo: "Novos", antigo: "Antigos", retorno: "Retorno (6+ meses)", retrabalho: "Retrabalho" };
const SOURCE_COLORS = ["#4ade80", "#60a5fa", "#facc15", "#fb7185", "#a78bfa", "#fb923c", "#38bdf8", "#f472b6", "#c084fc", "#94a3b8"];
const SOURCE_LBL = SOURCE_LABELS;

export function Section({ title, testid, children }) {
  return (
    <section data-testid={testid} className="space-y-3">
      <h2 className="flex items-center gap-3 font-display text-sm font-bold uppercase tracking-wider text-zinc-400">
        <span className="h-4 w-1 rounded-full bg-green-brand" />{title}<span className="flex-1 h-px bg-zinc-800" />
      </h2>
      {children}
    </section>
  );
}

export function CustomerMixChart({ mix }) {
  const keys = ["novo", "antigo", "retorno", "retrabalho"];
  const max = Math.max(1, ...keys.map((k) => mix?.[k] || 0));
  return (
    <section data-testid="chart-customer-mix" className="h-full rounded-2xl border border-card2-border bg-card2 p-5">
      <h3 className="font-display text-base font-bold uppercase text-zinc-100 mb-4">Clientes por carro finalizado</h3>
      <div className="flex items-end gap-4">
        {keys.map((k) => {
          const v = mix?.[k] || 0;
          const h = Math.round((v / max) * 100);
          return (
            <div key={k} data-testid={`chart-mix-${k}`} className="flex-1 flex flex-col items-center gap-2">
              <div className="font-display text-xl font-black text-zinc-100">{v}</div>
              <div className="w-full h-32 flex items-end">
                <div className="w-full rounded-t-md transition-[height] duration-500" style={{ height: `${h}%`, minHeight: 4, background: MIX_COLORS[k], boxShadow: `0 -8px 20px -8px ${MIX_COLORS[k]}` }} />
              </div>
              <div className="text-[11px] font-mono-caps text-zinc-400 text-center leading-tight">{MIX_LABELS[k]}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function SourceDonutChart({ bySource }) {
  const incoming = (bySource || []).reduce((m, s) => { m[s.key || s.source || "nao_informado"] = s.count || 0; return m; }, {});
  const KEYS = ["google", "indicacao", "redes_sociais", "passou_na_frente", "pos_venda", "ja_cliente", "importado", "outro", "nao_informado"];
  const rows = [...KEYS, ...Object.keys(incoming).filter((k) => !KEYS.includes(k))].map((k) => ({ key: k, label: SOURCE_LBL[k] || k, count: incoming[k] || 0 }));
  const items = rows.filter((s) => s.count > 0);
  const total = items.reduce((s, i) => s + i.count, 0);
  if (!total) {
    return (
      <section data-testid="chart-source-donut" className="h-full rounded-2xl border border-card2-border bg-card2 p-5">
        <h3 className="font-display text-base font-bold uppercase text-zinc-100 mb-2">Origem dos clientes</h3>
        <p className="text-sm text-zinc-500">Sem clientes no período.</p>
        <ul className="mt-3 grid grid-cols-2 gap-1 text-[11px] text-zinc-500">{rows.map((r) => <li key={r.key} data-testid={`source-row-${r.key}`}>{r.label}<span className="ml-2 text-zinc-600">0</span></li>)}</ul>
      </section>
    );
  }
  const R = 48, cx = 56, cy = 56;
  let acc = 0;
  const arcs = items.map((it, i) => {
    const pct = it.count / total;
    const a0 = acc * 2 * Math.PI;
    acc += pct;
    const a1 = acc * 2 * Math.PI;
    const large = pct > 0.5 ? 1 : 0;
    const x0 = cx + R * Math.sin(a0), y0 = cy - R * Math.cos(a0);
    const x1 = cx + R * Math.sin(a1), y1 = cy - R * Math.cos(a1);
    return { key: it.key, color: SOURCE_COLORS[i % SOURCE_COLORS.length], path: `M ${cx} ${cy} L ${x0} ${y0} A ${R} ${R} 0 ${large} 1 ${x1} ${y1} Z`, label: it.label || SOURCE_LBL[it.key] || it.key, count: it.count, pct: (pct * 100).toFixed(0) };
  });
  return (
    <section data-testid="chart-source-donut" className="h-full rounded-2xl border border-card2-border bg-card2 p-5">
      <h3 className="font-display text-base font-bold uppercase text-zinc-100 mb-4">Origem dos clientes</h3>
      <div className="flex items-center gap-5 flex-wrap">
        <svg width="112" height="112" viewBox="0 0 112 112">
          {arcs.map((a, i) => (arcs.length === 1
            ? <circle key={i} cx={cx} cy={cy} r={R} fill={a.color}><title>{`${a.label}: ${a.count} (${a.pct}%)`}</title></circle>
            : <path key={i} d={a.path} fill={a.color} className="transition-opacity hover:opacity-80"><title>{`${a.label}: ${a.count} (${a.pct}%)`}</title></path>))}
          <circle cx={cx} cy={cy} r={26} fill="rgb(15 23 27)" />
          <text x={cx} y={cy + 5} textAnchor="middle" fill="#f4f4f5" fontWeight="900" fontSize="20">{total}</text>
        </svg>
        <ul className="flex-1 min-w-[12rem] space-y-1 text-sm">
          {rows.map((r) => {
            const a = arcs.find((x) => x.key === r.key);
            return (
              <li key={r.key} data-testid={`source-legend-${r.key}`} className={`flex items-center gap-2 ${a ? "" : "opacity-50"}`}>
                <span className="h-3 w-3 rounded-sm" style={{ background: a ? a.color : "#3f3f46" }} />
                <span className="text-zinc-200 flex-1">{r.label}</span>
                <span className="text-zinc-400 tabular-nums">{r.count}</span>
                <span className="text-xs text-zinc-500 tabular-nums">{a ? a.pct : 0}%</span>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

export function MechanicPerformance({ rows }) {
  const { hide } = useHideValues();
  return (
    <section data-testid="mechanic-performance" className="rounded-2xl border border-card2-border bg-card2 p-5">
      <h3 className="font-display text-base font-bold uppercase text-zinc-100 mb-4">Mecânicos — desempenho do mês</h3>
      {!rows?.length && <p className="text-sm text-zinc-500">Cadastre mecânicos em Funcionários (RH) e defina a meta mensal de cada um.</p>}
      <ul className="space-y-3">
        {(rows || []).map((m) => {
          const pct = m.goal > 0 ? Math.min(140, Math.round((m.produced / m.goal) * 100)) : 0;
          const color = pct >= 100 ? "#4ade80" : pct >= 60 ? "#facc15" : "#f87171";
          return (
            <li key={m.name} data-testid={`mech-perf-${m.name}`}>
              <div className="flex items-center justify-between text-sm">
                <span className="font-bold text-zinc-100 truncate">{m.name}</span>
                <span className="text-xs text-zinc-400 tabular-nums">{hide ? "•••••" : brl(m.produced)}{m.goal > 0 ? ` / ${hide ? "•••••" : brl(m.goal)}` : " — sem meta"}</span>
              </div>
              <div className="mt-1 h-2 rounded-full bg-zinc-900 overflow-hidden">
                <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
              </div>
              {m.goal > 0 && <div className="mt-0.5 text-[11px] text-zinc-500">{pct}% da meta{pct >= 100 ? " · meta batida!" : ""}</div>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
